import { Percomatic } from '@biggby-coffee/percomatic-typescript';
import { atom, selector } from 'recoil';
import { transItemState, modifyScreenHandlerState } from '../atoms';
import { setWrapper } from '../utils/setWrapper';

import { ActiveOptionsState, IngredientWithComponent } from './types';

const activeOptionsStateDefault: ActiveOptionsState = {
	isSizeOptionActive: false,
	isProductVersionOptionActive: false,
	activeModifierGroup: -1,
	activeModifierScale: -1
};

/**
 * tracks which option is active
 */
const activeOptionsState = atom<ActiveOptionsState>({
	key: 'activeOption',
	default: activeOptionsStateDefault
});

/**
 * tracks if sizes are open
 */
export const isSizeOptionActiveState = selector<boolean>({
	key: 'isSizeOptionActive',
	get({ get }) {
		const { isSizeOptionActive } = get(activeOptionsState);

		return isSizeOptionActive;
	},
	set: setWrapper(
		activeOptionsState,
		({ set, reset }, isSizeOptionActive) => {
			if (isSizeOptionActive) {
				set(activeOptionsState, {
					...activeOptionsStateDefault,
					isSizeOptionActive: true
				});
			} else {
				reset(activeOptionsState);
			}
		}
	)
});

/**
 * tracks if product versions are open
 */
export const isProductVersionOptionActiveState = selector<boolean>({
	key: 'isProductVersionOptionActive',
	get({ get }) {
		const { isProductVersionOptionActive } = get(activeOptionsState);

		return isProductVersionOptionActive;
	},
	set: setWrapper(
		activeOptionsState,
		({ set, reset }, isProductVersionOptionActive) => {
			if (isProductVersionOptionActive) {
				set(activeOptionsState, {
					...activeOptionsStateDefault,
					isProductVersionOptionActive: true
				});
			} else {
				reset(activeOptionsState);
			}
		}
	)
});

/**
 * tracks the active modifier scale. -1 if none
 */
export const activeModifierScaleState = selector<number>({
	key: 'activeModifierScale',
	get({ get }) {
		const { activeModifierScale } = get(activeOptionsState);

		return activeModifierScale;
	},
	set: setWrapper(activeOptionsState, ({ set }, activeModifierScale) => {
		set(activeOptionsState, {
			...activeOptionsStateDefault,
			activeModifierScale
		});
	})
});

/**
 * tracks the active modifier group. -1 if none
 */
export const activeModifierGroupState = selector<number>({
	key: 'activeModifierGroup',
	get({ get }) {
		const { activeModifierGroup } = get(activeOptionsState);

		return activeModifierGroup;
	},
	set: setWrapper(activeOptionsState, ({ set }, activeModifierGroup) => {
		set(activeOptionsState, {
			...activeOptionsStateDefault,
			activeModifierGroup
		});
	})
});

/**
 * prevents multiple modifications at the same time
 */
export const modifyingState = atom<boolean>({
	key: 'modifying',
	default: false
});

export const componentsState = selector<Percomatic.Serialized.Component[]>({
	key: 'components',
	get({ get }) {
		const transItem = get(transItemState);

		if (!transItem) return [];

		const {
			item: { recipe: ingredients },
			components: transComponents
		} = transItem;

		const filteredTransComponents = transComponents.filter(
			({ component: { id } }) =>
				!ingredients.some(
					ingredient =>
						ingredient.component && ingredient.component.id === id
				)
		);

		const components = [
			...ingredients
				.filter(
					(ingredient): ingredient is IngredientWithComponent =>
						ingredient.component !== undefined &&
						ingredient.component !== null &&
						ingredient.quantity > 0
				)
				.map(({ component }) => component),
			...filteredTransComponents.map(({ component }) => component)
		];

		return components;
	}
});

export const hasNutritionState = selector<boolean>({
	key: 'hasNutrition',
	get() {
		// TODO: compute an actual value here. we might need to add something to
		// perc to do this
		return true;
	}
});

export const sizeState = selector<Percomatic.Serialized.Size | undefined>({
	key: 'size',
	get({ get }) {
		const transItem = get(transItemState);

		return transItem?.item.size;
	}
});

/**
 * gets "ContextProperty" "modifierScales"
 */
export const modifierScalesState =
	selector<Percomatic.Serialized.ModifierScaleModel>({
		key: 'modifierScales',
		get({ get }) {
			const modifyScreenHandler = get(modifyScreenHandlerState);

			return (
				modifyScreenHandler.modifierScales ?? {
					modifierScales: [],
					modifierScaleIndices: []
				}
			);
		}
	});

/**
 * gets "ContextProperty" "modifierGroups"
 */
export const modifierGroupsState =
	selector<Percomatic.Serialized.ModifierGroupModel>({
		key: 'modifierGroups',
		get({ get }) {
			const modifyScreenHandler = get(modifyScreenHandlerState);

			return (
				modifyScreenHandler.modifierGroups ?? {
					modifierGroups: [],
					modifierModels: []
				}
			);
		}
	});

/**
 * gets "ContextProperty" "sizes"
 */
export const sizesState = selector<Percomatic.Serialized.SizeModel>({
	key: 'sizes',
	get({ get }) {
		const modifyScreenHandler = get(modifyScreenHandlerState);

		return modifyScreenHandler.sizes;
	}
});

/**
 * gets "ContextProperty" "productVersions"
 */
export const productVersionsState =
	selector<Percomatic.Serialized.ProductVersionModel>({
		key: 'productVersions',
		get({ get }) {
			const modifyScreenHandler = get(modifyScreenHandlerState);

			return modifyScreenHandler.productVersions;
		}
	});

/**
 * checks if the "ContextProperty" "modifierScales" exists
 */
export const hasModifierScalesState = selector<boolean>({
	key: 'hasModifierScales',
	get({ get }) {
		const modifierScales = get(modifierScalesState);

		return modifierScales.modifierScales.length > 0;
	}
});

/**
 * checks if the "ContextProperty" "modifierGroups" exists
 */
export const hasModifierGroupsState = selector<boolean>({
	key: 'hasModifierGroups',
	get({ get }) {
		const modifierGroups = get(modifierGroupsState);

		return modifierGroups.modifierGroups.length > 0;
	}
});

/**
 * checks if the "ContextProperty" "sizes" exists
 */
export const hasSizesState = selector<boolean>({
	key: 'hasSizes',
	get({ get }) {
		const sizes = get(sizesState);

		return sizes.sizes.length > 0;
	}
});

/**
 * checks if the "ContextProperty" "productVersions" exists
 */
export const hasProductVersionsState = selector<boolean>({
	key: 'hasProductVersions',
	get({ get }) {
		const productVersions = get(productVersionsState);

		return productVersions.productVersions.length > 0;
	}
});

export const currentVersionState = selector<
	Percomatic.Serialized.ProductVersion | undefined
>({
	key: 'currentVersion',
	get({ get }) {
		const transItem = get(transItemState);

		return transItem?.item.product?.productVersion;
	}
});

export const transModifiersState = selector<
	Percomatic.Serialized.TransModifier[]
>({
	key: 'transModifiers',
	get({ get }) {
		const transItem = get(transItemState);

		return transItem?.modifiers ?? [];
	}
});

export const transItemProductState = selector<
	Percomatic.Serialized.Product | undefined
>({
	key: 'transItemProduct',
	get({ get }) {
		const transItem = get(transItemState);

		return transItem?.item.product;
	}
});
